/**
 * F 429 — per-block encryption inside a page.
 *
 * The engine (F 233) seals an `EncryptedNode` by `id`; the editor stores
 * Blocks (F 131/F 145) with their own ids under `freshcards:content:<pageId>`.
 * Because every Block is uniquely-id'd and uniquely-pageId'd, encrypting ONE
 * block reduces to:
 *
 *   1. read Block[] via loadPageContent
 *   2. find by id, serialise its content to plaintext
 *   3. call encryptNode(blockId, passphrase, store)
 *   4. replace the block with a STUB that says "🔒 [encrypted block]"
 *   5. write Block[] back via savePageContent
 *
 * `freshcards:encrypted-block:<pageId>:<blockId>` holds the sealed envelope.
 * Anchors live on the Block itself (as `attributes.anchors`), plaintext, so
 * the AI can search for the block by hint without opening it.
 *
 * DECISIONS:
 *   - The stub is what the editor renders. We do NOT leave the ciphertext
 *     in `Block.content` because BlockNote requires `InlineNode[]` there;
 *     a cipher envelope breaks the editor's render and saves as garbage.
 *   - Anchors live on the Block under `attributes.anchors` because the
 *     BlockNote editor preserves unknown attribute keys (F 117 serialiser).
 *   - The encrypted body is stored under a SEPARATE key, NOT inside the
 *     Block, so a failed re-serialisation can never leak plaintext.
 *   - `setNodeStore` is installed ONCE here in module scope, mirroring the
 *     pattern in `PageTree.tsx` (F 422). The store is name-keyed; we use the
 *     `freshcards:encrypted-block:<pageId>:<blockId>` namespace.
 *
 * The shapes are ENGINE-SHAPED on purpose: the engine accepts any
 * `{ id, content, encrypted, anchors }` (its `EncryptedNode` interface has
 * an index signature). A block cipher envelope keys on `id` alone.
 */
import { encryptNode, decryptNode, setNodeStore, type EncryptedNode, type NodeStore } from './vault';
import { setAnchors, type Anchor, type AnchorStore } from '../anchors';
import { loadPageContent, savePageContent } from '../workspace/pageContentStorage';
import { RemoteState } from '../storage/RemoteState';

const BLOCK_PREFIX = 'freshcards:encrypted-block:';

/** What the editor renders for an encrypted block. BlockNote-shaped. */
export interface EncryptedBlockStub {
  id: string;
  type: 'paragraph';
  content: Array<{ type: 'text'; text: string; styles: Record<string, never> }>;
  children: [];
  attributes: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
  /** True when sealed; mirrors `BaseEntity.encrypted`. */
  encrypted: true;
  /** Plaintext search hints — the AI reads only this. */
  anchors: Anchor[];
}

/** The envelope we keep alongside the page's Block[] array. */
interface SealedBody {
  envelope: unknown;           // CipherEnvelope from vault.ts
  plaintextPreview: string;    // first 80 chars for "the operator said something like"
  savedAt: string;             // ISO timestamp
  algorithm: string;
}

// ─── engine-shaped store ──────────────────────────────────────────────────

/**
 * The store the engine reads/writes. One instance at module scope — the
 * same pattern F 422's `PageTree.tsx` uses (F 422 §19-#457 — "a dependency
 * installed by a component that never mounts is not installed"). A second
 * instance with no caller would have left this engine unusable, identical
 * to the panel engine: the install is here because this is the first real
 * caller the editor has ever had.
 */
const inMemoryNodeStore: NodeStore & { data: Map<string, EncryptedNode> } = {
  data: new Map<string, EncryptedNode>(),
  async get(id) { return this.data.get(id); },
  async set(id, node) { this.data.set(id, node); },
};
setNodeStore(inMemoryNodeStore);

// ─── the page-content adapter ────────────────────────────────────────────

function findBlockIndex(blocks: any[], blockId: string): number {
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i]?.id === blockId) return i;
  }
  return -1;
}

/**
 * The plaintext string for a block — what the operator would see rendered.
 * We serialise `content` the same way `vault.ts#serialise` does, and use a
 * short preview so an accidental log line does not leak the secret.
 */
function blockPlaintext(block: any): string {
  const inline = Array.isArray(block?.content) ? block.content : [];
  const text = inline.map((n: any) => (typeof n?.text === 'string' ? n.text : '')).join('');
  return text;
}

/**
 * Persist a sealed body under its own key. EncryptedBlock `<pageId>:<id>` so
 * the engine's `get(id)` reads back what `set(id)` wrote. The key is NOT
 * any nodeId the editor knows about — it is a backend key, separate from the
 * Block that the editor renders.
 */
function sealKey(pageId: string, blockId: string): string {
  return `${BLOCK_PREFIX}${pageId}:${blockId}`;
}

function readSealed(pageId: string, blockId: string): SealedBody | null {
  const raw = RemoteState.get(sealKey(pageId, blockId));
  if (typeof raw !== 'string' || !raw) return null;
  try { return JSON.parse(raw) as SealedBody; } catch { return null; }
}

function writeSealed(pageId: string, blockId: string, body: SealedBody): void {
  RemoteState.set(sealKey(pageId, blockId), JSON.stringify(body));
}

function deleteSealed(pageId: string, blockId: string): void {
  RemoteState.remove(sealKey(pageId, blockId));
}

// ─── public API ──────────────────────────────────────────────────────────

export interface EncryptBlockArgs {
  pageId: string;
  blockId: string;
  passphrase: string;
  anchor: string;
}

export interface DecryptBlockArgs {
  pageId: string;
  blockId: string;
  passphrase: string;
}

export type BlockCryptoResult<T = void> =
  | { ok: true; value: T }
  | { ok: false; error: string };

const ok = <T,>(value: T): BlockCryptoResult<T> => ({ ok: true, value });
const fail = (error: string): BlockCryptoResult<never> => ({ ok: false, error });

/**
 * Encrypt a single block.
 *
 * Replaces the block in the page's `Block[]` with a stub (so BlockNote still
 * renders something), keeps the plaintext around as a sealed body in
 * RemoteState under `freshcards:encrypted-block:<pageId>:<blockId>`, and
 * writes the anchors onto the stub.
 *
 * The engine is doing what it always does — sealing an `EncryptedNode` by id —
 * but the node lives in our in-memory map instead of in `freshcards:pages`
 * like a page-encrypted entity. This is the same shape as the page engine,
 * which is the load-bearing property for "the same engine seals every node."
 */
export async function encryptBlock(args: EncryptBlockArgs): Promise<BlockCryptoResult<EncryptedBlockStub>> {
  const { pageId, blockId, passphrase, anchor } = args;

  if (!pageId) return fail('pageId is required');
  if (!blockId) return fail('blockId is required');
  if (!passphrase) return fail('a passphrase is required');
  if (!anchor || typeof anchor !== 'string' || anchor.trim().length === 0) {
    return fail('an anchor is required (the AI uses it to find this block without reading it)');
  }

  const blocks = loadPageContent(pageId);
  if (!Array.isArray(blocks)) return fail(`page "${pageId}" has no content`);
  const idx = findBlockIndex(blocks, blockId);
  if (idx < 0) return fail(`no block with id "${blockId}" in page "${pageId}"`);

  const original = blocks[idx];
  const existing = (original?.attributes?.encrypted as unknown) === true;
  if (existing) return fail(`block "${blockId}" is already encrypted — decrypt it first`);

  const plaintext = blockPlaintext(original);
  if (!plaintext.trim()) return fail(`block "${blockId}" has no content to encrypt`);

  // ── seal the body under a backend id ───────────────────────────────
  //
  // We use the pageId+blockId COMPACT as the engine's id so that a future
  // search that walks every encrypted block can reach the same node by
  // asking the engine. The engine does not care what the id means.
  //
  // F 429 — ORDER MATTERS AND IT IS NOT OBVIOUS. `encryptNode` is
  // `store.get(id)` → serialise(`node.content`) → seal → `store.set(id, …)`.
  // It READS the plaintext out of the store; it does not take plaintext as an
  // argument. So the plaintext node must be WRITTEN first, and only then
  // handed to the engine, which replaces `.content` with the envelope. Passing
  // an id with nothing behind it — which is what this module's first draft did
  // — fails with `no node with id "page:block"` and, if the failure had been
  // swallowed, would have shipped a stub with the secret still in the Block.
  const engineId = `${pageId}:${blockId}`;
  inMemoryNodeStore.data.set(engineId, {
    id: engineId,
    content: plaintext,
    encrypted: false,
    anchors: [],
  });
  const sealed = await encryptNode(engineId, passphrase);
  if (!sealed.ok) {
    // Leave nothing behind: a plaintext node in the map is a plaintext secret
    // in memory that outlives the failed attempt.
    inMemoryNodeStore.data.delete(engineId);
    return fail((sealed as { ok: false; error: string }).error);
  }

  // Persist the envelope separately so a missing savePageContent does not
  // leak plaintext into the Block[].
  writeSealed(pageId, blockId, {
    envelope: (sealed as { ok: true; value: unknown }).value,
    plaintextPreview: plaintext.slice(0, 80),
    savedAt: new Date().toISOString(),
    algorithm: 'AES-GCM-256',
  });

  // ── build the stub that the editor will render ────────────────────
  const now = new Date();
  const anchors: Anchor[] = [{
    id: `${blockId}:anchor:${Date.now()}`,
    text: anchor.trim(),
    kind: 'hint',
    source: 'user',
    confidence: 1,
    createdAt: now.toISOString(),
  }];
  const stub: EncryptedBlockStub = {
    id: blockId,
    type: 'paragraph',
    content: [{ type: 'text', text: '🔒 [encrypted block]', styles: {} as Record<string, never> }],
    children: [],
    attributes: { encrypted: true, anchor: anchor.trim(), hasCipher: true },
    createdAt: original?.createdAt instanceof Date ? original.createdAt : now,
    updatedAt: now,
    encrypted: true,
    anchors,
  };

  // ── replace the block in the page's Block[] array ─────────────────
  const next = [...blocks];
  next[idx] = stub;
  savePageContent(pageId, next);

  // Persist anchors via the same setAnchors the engine already exposes, on
  // the same in-memory node — so a follow-up search sees them.
  //
  // F 429 — the cast is deliberate and narrow. `anchors.ts` declares its own
  // `AnchorStore` (`{get,set}` over `AnchorNode`), and `vault.ts` declares
  // `NodeStore` (`{get,set}` over `EncryptedNode`). The two node shapes are
  // NOT identical, so neither store satisfies the other's interface without a
  // cast — but they are the SAME in-memory map, and `setAnchors` only ever
  // touches the `anchors` field, which both shapes have. The alternative is a
  // duplicate store, and two stores over one map is the bug, not the cast.
  await setAnchors(engineId, anchors, inMemoryNodeStore as unknown as AnchorStore);

  return ok(stub);
}

/**
 * Decrypt a single block.
 *
 * Looks up the sealed body, calls decryptNode, restores the original block
 * shape (so BlockNote renders it as before — type, content, attributes),
 * and clears the stub entry.
 *
 * If the passphrase is wrong: the engine returns `OperationError`; we
 * surface the message verbatim. No plaintext is ever returned without a
 * successful seal check.
 */
export async function decryptBlock(args: DecryptBlockArgs): Promise<BlockCryptoResult<{ plaintext: string }>> {
  const { pageId, blockId, passphrase } = args;

  if (!pageId) return fail('pageId is required');
  if (!blockId) return fail('blockId is required');
  if (!passphrase) return fail('a passphrase is required');

  const blocks = loadPageContent(pageId);
  if (!Array.isArray(blocks)) return fail(`page "${pageId}" has no content`);
  const idx = findBlockIndex(blocks, blockId);
  if (idx < 0) return fail(`no block with id "${blockId}" in page "${pageId}"`);

  const current = blocks[idx];
  if ((current?.attributes?.encrypted as unknown) !== true) {
    return fail(`block "${blockId}" is not encrypted`);
  }

  const sealed = readSealed(pageId, blockId);
  if (!sealed) return fail(`no sealed body for block "${blockId}" — was the page migrated?`);

  // ── open the envelope (sealed.envelope is the engine's CipherEnvelope)
  const engineId = `${pageId}:${blockId}`;
  // Re-load the engine's node from the in-memory map so decryptNode sees it.
  inMemoryNodeStore.data.set(engineId, {
    id: engineId,
    content: sealed.envelope,
    encrypted: true,
    anchors: (current?.anchors ?? []) as unknown[],
  });
  const opened = await decryptNode(engineId, passphrase);
  if (!opened.ok) {
    return fail((opened as { ok: false; error: string }).error);
  }
  const plaintext = String((opened as { ok: true; value: unknown }).value);

  // ── restore: the original block had content encoded as a joined string
  //
  // We restore as a single paragraph with the same `inline` shape so
  // BlockNote round-trips the original behaviour. `attributes` preserved
  // except `encrypted` and `hasCipher` are gone.
  const restored: any = {
    id: blockId,
    type: current?.type ?? 'paragraph',
    content: [{ type: 'text', text: plaintext, styles: {} }],
    children: Array.isArray(current?.children) ? current.children : [],
    attributes: { ...(current?.attributes ?? {}) } as Record<string, unknown>,
    createdAt: current?.createdAt instanceof Date ? current.createdAt : new Date(),
    updatedAt: new Date(),
  };
  delete (restored.attributes as Record<string, unknown>).encrypted;
  delete (restored.attributes as Record<string, unknown>).hasCipher;

  const next = [...blocks];
  next[idx] = restored;
  savePageContent(pageId, next);

  deleteSealed(pageId, blockId);
  inMemoryNodeStore.data.delete(engineId);

  return ok({ plaintext });
}

/**
 * Read-only: does this block currently appear encrypted?
 *
 * Cheap DOM-style probe — saves the round-trip through encryptBlock just to
 * see the state.
 */
export function isBlockEncrypted(pageId: string, blockId: string): boolean {
  const blocks = loadPageContent(pageId);
  if (!Array.isArray(blocks)) return false;
  const idx = findBlockIndex(blocks, blockId);
  if (idx < 0) return false;
  return (blocks[idx]?.attributes?.encrypted as unknown) === true;
}

/**
 * Read-only: the plaintext anchors the operator wrote on this block, or [].
 */
export function blockAnchors(pageId: string, blockId: string): Anchor[] {
  const blocks = loadPageContent(pageId);
  if (!Array.isArray(blocks)) return [];
  const idx = findBlockIndex(blocks, blockId);
  if (idx < 0) return [];
  return (blocks[idx]?.anchors as Anchor[] | undefined) ?? [];
}

/**
 * F 487 — write the block's public anchors.
 *
 * This is the "adjust that tag" step of the operator's flow: the tag is
 * created when the block is sealed, and the operator can change it
 * afterwards without knowing the passphrase — the anchor is PLAINTEXT by
 * design (see `BaseEntity.anchors`), because its whole purpose is to let a
 * reader without the key find the block.
 *
 * The anchors are written to BOTH places that hold them, and they have to
 * agree:
 *
 *   1. the page's `Block[]` — so the stored block carries the tag, and so
 *      the editor's autosave keeps it (see `mergeSealedBlocks`);
 *   2. the engine's node — so `searchByAnchors` finds the block by its new
 *      tag, which is the entire point of a tag.
 *
 * Writing only the first would leave the search reading the old tag, and
 * the operator would see a tag that does not work.
 *
 * The block MUST already be sealed: an unsealed block has no tag to adjust,
 * and pretending otherwise would write a tag onto plaintext content — which
 * would make the content searchable, defeating the seal.
 */
export async function setBlockAnchors(
  args: { pageId: string; blockId: string; anchors: Anchor[] },
): Promise<BlockCryptoResult<Anchor[]>> {
  const { pageId, blockId, anchors } = args;
  if (!pageId) return fail('pageId is required');
  if (!blockId) return fail('blockId is required');
  if (!Array.isArray(anchors)) return fail('anchors must be an array');

  const blocks = loadPageContent(pageId);
  if (!Array.isArray(blocks)) return fail(`page "${pageId}" has no content`);
  const idx = findBlockIndex(blocks, blockId);
  if (idx < 0) return fail(`no block with id "${blockId}" in page "${pageId}"`);

  const current = blocks[idx];
  if ((current?.attributes?.encrypted as unknown) !== true) {
    return fail(`block "${blockId}" is not sealed — a tag can only be adjusted on a sealed block`);
  }

  // Normalise: an anchor needs a text, and the operator's edit is the
  // authority for it. Blank anchors are dropped rather than stored, because
  // a blank tag is not a tag.
  const cleaned: Anchor[] = anchors
    .filter((a) => a && typeof a.text === 'string' && a.text.trim().length > 0)
    .map((a, i) => ({
      id: a.id || `${blockId}:anchor:${Date.now()}:${i}`,
      text: a.text.trim(),
      kind: a.kind ?? 'hint',
      source: a.source ?? 'user',
      confidence: typeof a.confidence === 'number' ? a.confidence : 1,
      createdAt: a.createdAt ?? new Date().toISOString(),
    }));

  const next = [...blocks];
  // The tag lives in TWO fields on the block, and they must agree:
  //   `attributes.anchor` — the denormalised single tag the UI reads;
  //   `anchors[]`        — the full list, which is what the search reads.
  // Updating only the list would leave the UI showing the OLD tag while
  // the search finds the NEW one — the worst kind of half-working.
  next[idx] = {
    ...current,
    attributes: {
      ...(current.attributes ?? {}),
      ...(cleaned[0]?.text ? { anchor: cleaned[0].text } : {}),
    },
    anchors: cleaned,
  };
  savePageContent(pageId, next);

  // The engine's node carries the same anchors, so the search sees them.
  const engineId = `${pageId}:${blockId}`;
  const node = inMemoryNodeStore.data.get(engineId);
  if (node) {
    await inMemoryNodeStore.set(engineId, { ...node, anchors: cleaned } as EncryptedNode);
  }
  await setAnchors(engineId, cleaned, inMemoryNodeStore as unknown as AnchorStore);

  return ok(cleaned);
}

/** Test affordance: clear the in-memory node map. Production NEVER calls this. */
export function _resetBlockEncryptionForTests(): void {
  inMemoryNodeStore.data.clear();
}

/**
 * F 487 — the engine's node store, for a test that needs to SEARCH by the
 * block's tag.
 *
 * The UI's `EncryptionPanel` has its OWN store (`memoryNodeStore`), and the
 * two are different maps — which is why the block's tags are read and
 * written through the block engine's own functions rather than through the
 * panel's anchor editor. This export exists so a test can prove the tag is
 * searchable, which is the whole point of a tag.
 *
 * `searchByAnchors` takes an ARRAY of nodes, not a store, so this returns
 * the node for one engine id.
 */
export function blockEncryptionNodeForTests(engineId: string): AnchorNode | undefined {
  return inMemoryNodeStore.data.get(engineId) as unknown as AnchorNode | undefined;
}
